import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import { paths } from 'config/path_helpers';
import { useQueries } from '@magoosh/gre/app/queries';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { useUserContext } from '@magoosh/context/user_context';
import { Row, Col } from 'react-bootstrap';

import { StudyPlanSettings } from './types';
import PlanSettingsPanel from './plan_settings_panel';
import { TabButton } from '@magoosh/lib/button';
import PrioritizedCheckpoint from '@magoosh/gre/organisms/checkpoints/prioritized';
import SideBar from '@magoosh/gre/adaptive_prep_v2/organisms/side_bar';
import StatusMessage from '@magoosh/gre/adaptive_prep_v2/lib/status_message';
import PresetPlan from './preset_plan';
import AdaptivePlan from './adaptive_plan';

export const StudyPlan: React.FC = () => {
  const tab = useParams().tab || 'plan';
  const q = useQueries();
  const { testDate } = useUserContext();

  const checkpointsQuery = q.checkpointsQuery();
  const sidebarQuery = q.sidebarQuery();
  const studyPlanQuery = q.studyPlanQuery();
  const createOrFindStudyPlanMutation = (params: StudyPlanSettings) => {
    q.createOrFindStudyPlanMutation.mutate(params);
  };

  const isLoadingStudyPlan = studyPlanQuery.isLoading || checkpointsQuery.isLoading || sidebarQuery.isLoading;
  const isPresetPlan = studyPlanQuery.data?.planType === 'preset';

  if (isLoadingStudyPlan) {
    // TODO - separate loading state for the study plan and the items themselves
    return (
      <StatusMessage headline="How exciting!" text="Your study plan is almost ready..." isColored={true} />
    );
  } else {
    const PrioritizedCheckpointComponent = checkpointsQuery.data?.prioritizedCheckpoint ? (
      <PrioritizedCheckpoint
        allowDefer={checkpointsQuery.data?.prioritizedCheckpoint.allowDefer}
        deferAssessment={() => q.createOrFindStudyPlanMutation.mutate({ planType: 'adaptive' })}
        dismissRefresh={() => q.dismissStudyPlanRefreshMutation.mutate()}
        prioritizedCheckpoint={checkpointsQuery.data?.prioritizedCheckpoint}
        refreshPlan={() => q.createOrFindStudyPlanMutation.mutate({ planType: 'adaptive' })}
        subsectionsToPrioritizeOnRefresh={checkpointsQuery.data?.prioritizedCheckpoint.subsectionsToPrioritizeOnRefresh}
        testDate={testDate}
      />
    ) : null;

    return (
      <div className="u-margin-T-l">
        <ContextualLink id="top"></ContextualLink>

        <Helmet>
          <title>Your Study Plan - Magoosh GRE</title>
        </Helmet>
        <Row>
          <Col sm={8}>
            <div className="d-flex align-items-start align-items-sm-center flex-column flex-sm-row justify-content-between">
              <h2 className="u-margin-T-n">
                <strong>
                  {/* TODO - add the study plan balance in the adaptive plan title */}
                  {isPresetPlan ? `Preset Study Plan: ${studyPlanQuery.data?.name}` : 'Adaptive Study Plan'}
                </strong>
              </h2>
              <PlanSettingsPanel
                currentSettings={studyPlanQuery.data?.options?.currentSettings}
                presetPlanOptions={studyPlanQuery.data?.options?.presetPlanOptions}
                onSubmit={createOrFindStudyPlanMutation}
                planType={studyPlanQuery.data?.options?.planType}
              />
            </div>
            <hr className="u-margin-A-n" />
            {
              isPresetPlan
              ? <>
                  <PresetPlanTabs currentTab={tab} />
                  <hr className="u-margin-A-n u-margin-B-s" />
                  <PresetPlan
                    allDayNumbers={studyPlanQuery.data?.allDayNumbers}
                    checkpoints={checkpointsQuery.data?.allCheckpoints}
                    currentDayNumber={studyPlanQuery.data?.currentDayNumber}
                    prioritizedCheckpoint={PrioritizedCheckpointComponent}
                    tab={tab}
                  />
                </>
              : <>
                  <AdaptivePlanTabs currentTab={tab} />
                  <hr className="u-margin-A-n u-margin-B-s" />
                  <AdaptivePlan
                    adaptiveHoursPerChunk={studyPlanQuery.data?.adaptiveHoursPerChunk}
                    checkpoints={checkpointsQuery.data?.allCheckpoints}
                    prioritizedCheckpoint={PrioritizedCheckpointComponent}
                    studyPlanIsPresent={!!studyPlanQuery.data?.id}
                    tab={tab}
                  />
                </>
            }
          </Col>

          <Col sm={4}>
            <SideBar
              hoursStudiedThisWeek={sidebarQuery.data?.hoursStudiedThisWeek}
              isLoading={isLoadingStudyPlan}
              notes={sidebarQuery.data?.notes}
              recommendedWeeklyHours={sidebarQuery.data?.recommendedWeeklyHours}
            />
          </Col>
        </Row>
      </div>
    );
  }
};

interface TabProps {
  currentTab: 'plan' | 'done' | 'checkpoints';
}

const AdaptivePlanTabs: React.FC<TabProps> = ({ currentTab }) => {
  return (
    <div>
      <ContextualLink href={paths.dashboard('done')}>
        <TabButton disabled={currentTab === 'done'}>Done</TabButton>
      </ContextualLink>
      <ContextualLink href={paths.dashboard()}>
        <TabButton disabled={currentTab === 'plan'}>To Do</TabButton>
      </ContextualLink>
      <ContextualLink href={paths.dashboard('checkpoints')}>
        <TabButton disabled={currentTab === 'checkpoints'}>Checkpoints</TabButton>
      </ContextualLink>
    </div>
  );
};

const PresetPlanTabs: React.FC<TabProps> = ({ currentTab }) => {
  return (
    <div>
      <ContextualLink href={paths.dashboard()}>
        <TabButton disabled={currentTab === 'plan'}>Study Plan</TabButton>
      </ContextualLink>
      <ContextualLink href={paths.dashboard('checkpoints')}>
        <TabButton disabled={currentTab === 'checkpoints'}>Checkpoints</TabButton>
      </ContextualLink>
    </div>
  );
};

export default StudyPlan;
